/**
 * 2026-06-07 — Player-facing settings stored in localStorage.
 *
 * Two settings live here:
 *
 * 1. autoRollEnabled — when true, the QuickRollButton auto-fires once
 *    on mount for any paragraph that detects a roll trigger. The dice
 *    cinematic still plays so the player sees the resolution.
 *
 * 2. helpVisibleEnabled — when true, the inline "?" help affordances on
 *    UI elements are visible. Pressing the help-toggle on the header
 *    flips this for the whole session.
 *
 * Both default to false (off) so the existing manual flow is preserved
 * for players who don't opt in. The toggles are surfaced in the
 * scenario-runner header for one-click switching at any time.
 */

import { useEffect, useState } from 'react';

const AUTO_ROLL_KEY = 'briefing-room-auto-roll-enabled';
const HELP_VISIBLE_KEY = 'briefing-room-help-visible-enabled';

const settingsListeners = new Set<() => void>();

function notify() {
  for (const listener of settingsListeners) {
    listener();
  }
}

function readBool(key: string): boolean {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
    return false;
  }
  try {
    return localStorage.getItem(key) === 'true';
  } catch {
    return false;
  }
}

function writeBool(key: string, value: boolean): void {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
    return;
  }
  try {
    if (value) {
      localStorage.setItem(key, 'true');
    } else {
      localStorage.removeItem(key);
    }
  } catch {
    // quota / privacy mode — best-effort only
  }
  notify();
}

export function getAutoRollEnabled(): boolean {
  return readBool(AUTO_ROLL_KEY);
}

export function setAutoRollEnabled(value: boolean): void {
  writeBool(AUTO_ROLL_KEY, value);
}

export function getHelpVisibleEnabled(): boolean {
  return readBool(HELP_VISIBLE_KEY);
}

export function setHelpVisibleEnabled(value: boolean): void {
  writeBool(HELP_VISIBLE_KEY, value);
}

/** Subscribe a component to the settings store. Returns current values. */
export function usePlayerSettings(): {
  autoRollEnabled: boolean;
  helpVisibleEnabled: boolean;
  setAutoRollEnabled: (v: boolean) => void;
  setHelpVisibleEnabled: (v: boolean) => void;
} {
  const [autoRollEnabled, setAutoRoll] = useState<boolean>(getAutoRollEnabled);
  const [helpVisibleEnabled, setHelpVisible] = useState<boolean>(
    getHelpVisibleEnabled,
  );

  useEffect(() => {
    const listener = () => {
      setAutoRoll(getAutoRollEnabled());
      setHelpVisible(getHelpVisibleEnabled());
    };
    settingsListeners.add(listener);
    return () => {
      settingsListeners.delete(listener);
    };
  }, []);

  return {
    autoRollEnabled,
    helpVisibleEnabled,
    setAutoRollEnabled: (v: boolean) => {
      setAutoRollEnabled(v);
    },
    setHelpVisibleEnabled: (v: boolean) => {
      setHelpVisibleEnabled(v);
    },
  };
}
